import React from "react";
import { CompassParagraph } from "../styledComponents";
import {
  ElementContainer,
  HorizontalLine,
  RowContainer,
} from "../../containers/styledComponents";
import { AIBatchLinkageResponse } from "../../../types/compassAITypes";
import { MESSAGES } from "../../../messages";
import successAnimation from "../../../assets/lottie/success_checkmark_animation.json";
import {
  compassDarBlue,
  compassLightBlue,
  compassLightGreen,
  compassYellow,
  brightRed,
  graphPaletteV2,
} from "../../../styles";
import Lottie from "react-lottie-player";
import { IconContainer } from "../../visuals/styledComponents";
import { KPIBox } from "../../visuals/KPIBox";
import { DataEntry } from "../../../models/stewardshipModels";
import calculatorIcon from "../../../assets/svg/metrics_icon.svg";
import { CompactDonutChart } from "../../charts/CompactDonutChart";
import { CompactBarChart } from "../../charts/CompactBarChart";
import { formatReadable } from "../../../utils/formattingUtils";

// =============================================================================
// TYPES
// =============================================================================

export interface BatchLinkageResultsSummaryProps {
  /** Batch linkage result data */
  result: AIBatchLinkageResponse;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Displays batch linkage analysis results summary with stats and charts.
 * Reusable component for showing AI batch linkage outcomes.
 */
export const BatchLinkageResultsSummary: React.FC<
  BatchLinkageResultsSummaryProps
> = ({ result }) => {
  const messages = MESSAGES.COMPASS_AI.BATCH_LINKAGE.SUMMARY;

  // Calculate totals for high confidence linkages
  const totalHighConfidence = result.company_results.reduce(
    (sum, company) => sum + company.high_confidence_linkages,
    0
  );

  // Build confidence distribution for donut chart
  const successfulCompanies = result.company_results.filter(
    (c) => c.status === "success"
  );

  const confidenceDistribution = {
    "High (≥70%)": successfulCompanies.filter(
      (c) => c.average_confidence >= 0.7
    ).length,
    "Medium (40-69%)": successfulCompanies.filter(
      (c) => c.average_confidence >= 0.4 && c.average_confidence < 0.7
    ).length,
    "Low (<40%)": successfulCompanies.filter(
      (c) => c.average_confidence > 0 && c.average_confidence < 0.4
    ).length,
  };

  // Build linkages per company distribution for bar chart (top 8 companies)
  const topCompanies = [...result.company_results]
    .filter((c) => c.linkages_found > 0)
    .sort((a, b) => b.linkages_found - a.linkages_found)
    .slice(0, 8);

  return (
    <>
      {/* Success Header */}
      <RowContainer jc="flex-start" ai="center" ml={-6}>
        <IconContainer size={46} m={1} p={1}>
          <Lottie loop={false} play animationData={successAnimation} />
        </IconContainer>
        <ElementContainer flexDirection="column">
          <CompassParagraph color={compassDarBlue} fontSize={14} bold>
            {MESSAGES.COMPASS_AI.BATCH_LINKAGE.DESCRIPTION.SUCCESS}
          </CompassParagraph>
          <ElementContainer flexDirection="row">
            <CompassParagraph color={compassDarBlue} mr={5}>
              Status:
            </CompassParagraph>
            <CompassParagraph color={compassLightBlue} mr={5}>
              {result.status}
            </CompassParagraph>
          </ElementContainer>
        </ElementContainer>
      </RowContainer>
      <HorizontalLine marginTop={4} />

      {/* Main KPIs */}
      <KPIBox
        icon={calculatorIcon}
        title="Analysis Results"
        kpis={
          [
            {
              label: messages.COMPANIES_PROCESSED,
              value: result.total_companies_processed,
            },
            {
              label: messages.SUCCESSFUL,
              value: result.successful_companies,
            },
            {
              label: messages.TOTAL_LINKAGES,
              value: result.total_linkages_found,
            },
            {
              label: messages.HIGH_CONFIDENCE,
              value: totalHighConfidence,
            },
          ] as DataEntry<number>[]
        }
      />

      {/* Additional Stats */}
      <RowContainer jc="flex-start" mt={8}>
        <ElementContainer flexDirection="row" ai="center" mr={40}>
          <CompassParagraph fontSize={13} mr={8}>
            {messages.PROCESSING_BATCHES}:
          </CompassParagraph>
          <CompassParagraph fontSize={13} bold>
            {result.processing_batches}
          </CompassParagraph>
        </ElementContainer>
        {result.failed_companies > 0 && (
          <ElementContainer flexDirection="row" ai="center">
            <CompassParagraph fontSize={13} mr={8} color="#ff4d4f">
              {messages.FAILED}:
            </CompassParagraph>
            <CompassParagraph fontSize={13} bold color="#ff4d4f">
              {result.failed_companies}
            </CompassParagraph>
          </ElementContainer>
        )}
      </RowContainer>

      {/* Charts Row */}
      <RowContainer jc="flex-start" ai="flex-start" mt={20}>
        {/* Confidence Distribution - Donut Chart */}
        {Object.values(confidenceDistribution).some((v) => v > 0) && (
          <ElementContainer flexDirection="column" mr={40}>
            <CompassParagraph fontSize={13} bold mb={8}>
              Confidence Distribution
            </CompassParagraph>
            <CompactDonutChart
              data={Object.entries(confidenceDistribution)
                .filter(([, count]) => count > 0)
                .map(([label, value]) => ({ label, value }))}
              colors={[compassLightGreen, compassYellow, brightRed]}
              size={140}
            />
          </ElementContainer>
        )}

        {/* Top Companies by Linkages - Bar Chart */}
        {topCompanies.length > 0 && (
          <ElementContainer flexDirection="column">
            <CompassParagraph fontSize={13} bold mb={8}>
              Top Companies by Linkages
            </CompassParagraph>
            <CompactBarChart
              data={topCompanies.map((c) => ({
                label: formatReadable(
                  c.company_name || c.compass_cid.slice(0, 10)
                ),
                value: c.linkages_found,
              }))}
              colors={graphPaletteV2}
              height={140}
              width="100px"
            />
          </ElementContainer>
        )}
      </RowContainer>
    </>
  );
};
